import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaPlay, FaStop } from "react-icons/fa";
import { fetchBellMusicForDropDown } from "../../common-services";
import { playBellPreview } from "../../utils/BellPreviewPlayer";

// The id used to identify a bell-music option. The /bell/music/active/min DTO
// exposes it as `value`; fall back to other likely keys defensively.
const getOptionId = (item) => item?.value ?? item?.id ?? item?.bellMusicId ?? null;

// Interactive bell-music picker: fetches the active bell-music dropdown, lets the
// user audition each track (remote-first, single preview at a time) and reports
// the chosen option back through onChange. `value` is the selected bellMusicId.
function BellMusicSelect({
    value = null,
    onChange,
    bellMusicList: bellMusicListProp,
    placeholder = "बेल संगीत चयन गर्नुहोस्",
    isDisabled = false,
    width = "100%",
}) {
    const [bellMusicList, setBellMusicList] = useState(bellMusicListProp || []);
    const [previewingId, setPreviewingId] = useState(null);
    const previewRef = useRef(null);

    useEffect(() => {
        if (bellMusicListProp) {
            setBellMusicList(bellMusicListProp);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await fetchBellMusicForDropDown();
                if (!cancelled && response?.status === 200) {
                    setBellMusicList(response.data || []);
                }
            } catch (e) {
                console.error("Error while fetching bell music dropdown:", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bellMusicListProp]);

    const stopPreview = () => {
        if (previewRef.current) {
            previewRef.current.stop();
            previewRef.current = null;
        }
        setPreviewingId(null);
    };

    // Stop any running preview when this picker unmounts.
    useEffect(() => () => stopPreview(), []);

    const togglePreview = (item) => {
        const id = getOptionId(item);
        if (previewingId === id) {
            stopPreview();
            return;
        }
        stopPreview();
        setPreviewingId(id);
        previewRef.current = playBellPreview(item?.url, {
            onEnded: () => {
                previewRef.current = null;
                setPreviewingId(null);
            },
            onError: () => {
                previewRef.current = null;
                setPreviewingId(null);
            },
            onStop: () => {
                previewRef.current = null;
                setPreviewingId(null);
            },
        });
    };

    const selected = bellMusicList.find((item) => getOptionId(item) === value);

    return (
        <Menu autoSelect={false} isLazy>
            <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                w={width}
                textAlign="left"
                fontWeight="normal"
                isDisabled={isDisabled}
            >
                {selected?.label || placeholder}
            </MenuButton>
            <MenuList maxH="240px" overflowY="auto" minW="var(--chakra-sizes-xs)">
                {bellMusicList.map((item) => {
                    const id = getOptionId(item);
                    const isPlaying = previewingId === id;
                    return (
                        <MenuItem
                            key={id}
                            onClick={() => onChange && onChange(item)}
                        >
                            <HStack justify="space-between" w="full" spacing={3}>
                                <Text noOfLines={1}>{item.label}</Text>
                                <IconButton
                                    size="sm"
                                    variant="ghost"
                                    borderRadius="full"
                                    colorScheme={isPlaying ? "red" : "blue"}
                                    aria-label={isPlaying ? "बजाउन रोक्नुहोस्" : "बेल बजाउनुहोस्"}
                                    icon={isPlaying ? <FaStop /> : <FaPlay />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePreview(item);
                                    }}
                                />
                            </HStack>
                        </MenuItem>
                    );
                })}
            </MenuList>
        </Menu>
    );
}

export default BellMusicSelect;
